/**
 * Crypto utilities specifically for the signup module
 * These functions are isolated from the main crypto utilities to avoid affecting other modules
 */

import { arrayBufferToBase64, arrayBufferFromBase64 } from '@/utilities/crypto';

/**
 * Derives a 256-bit key from a string using SHA-256
 * 
 * @param {string} keyString - The string to derive the key from
 * @returns {Promise<ArrayBuffer>} - A 256-bit key suitable for AES-GCM
 */
async function deriveKey(keyString) {
    // Convert the string to an ArrayBuffer using TextEncoder
    const keyData = new TextEncoder().encode(keyString);

    // Use SHA-256 to hash the key to ensure it's exactly 256 bits (32 bytes)
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', keyData);

    return hashBuffer;
}

/**
 * Encrypts data using AES-GCM with a derived key
 * 
 * @param {string} data - The data to encrypt
 * @param {string} key - The key to use for encryption (will be derived to 256 bits)
 * @returns {Promise<{encryptedData: ArrayBuffer, iv: Uint8Array}>} - The encrypted data and initialization vector
 */
export async function encryptSignupData(data, key) {
    const encodedData = new TextEncoder().encode(data);

    // Derive a proper 256-bit key
    const keyBuffer = await deriveKey(key);

    const cryptoKey = await window.crypto.subtle.importKey(
        'raw',
        keyBuffer,
        'AES-GCM',
        true,
        ['encrypt'],
    );
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encryptedData = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        cryptoKey,
        encodedData,
    );
    return { encryptedData, iv };
}

/**
 * Decrypts data using AES-GCM with a derived key
 * 
 * @param {ArrayBuffer} encryptedData - The data to decrypt
 * @param {Uint8Array} iv - The initialization vector used for encryption
 * @param {string} key - The key to use for decryption (will be derived to 256 bits)
 * @returns {Promise<string>} - The decrypted data as a string
 */
export async function decryptSignupData(encryptedData, iv, key) {
    try {
        // Derive a proper 256-bit key (same as in encryptSignupData)
        const keyBuffer = await deriveKey(key);

        const cryptoKey = await window.crypto.subtle.importKey(
            'raw',
            keyBuffer,
            'AES-GCM',
            true,
            ['decrypt'],
        );
        const decryptedArrayBuffer = await window.crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            cryptoKey,
            encryptedData,
        );
        return String.fromCharCode.apply(null, new Uint8Array(decryptedArrayBuffer));
    } catch (error) {
        console.error('Error decrypting signup data:', error);
        throw error;
    }
}

// Re-export the buffer conversion functions for convenience
export { arrayBufferToBase64, arrayBufferFromBase64 };